import { api } from '../../core/network/api';
import type { LootDrop } from './map-api';

export type MobAction = 'strike' | 'heavy' | 'guard' | 'ability' | 'flee';
export type CombatEvent = 'hit' | 'crit' | 'miss' | 'block' | 'ability' | null;

export interface RoundOutcome {
  round: number; action: MobAction; abilityName: string | null;
  heroDmg: number; heroEvent: CombatEvent; heroHeal: number;
  mobDmg: number; mobEvent: CombatEvent; bleedDmg: number;
  heroHp: number; mobHp: number; cooldown: number;
  fled: boolean; fleeFailed: boolean; ended: boolean; victory: boolean;
}

export interface FightView {
  active: boolean;
  round: number;
  cooldown: number;
  ability: { name: string; desc: string; ready: boolean };
  hero: { hp: number; maxHp: number; class: string };
  enemy: { name: string; type: string; level: number; archetype?: string; hp: number; maxHp: number };
  healsLeft: number;
  /** Mid-fight patch-up: its cash cost, and whether the player can currently
   *  afford it (balance ≥ cost, a heal remaining, and not already at full HP).
   *  `healAffordable` lets the client grey the card out instead of letting the
   *  player click into an "Insufficient balance" error. */
  healCost: number;
  healAffordable: boolean;
  /** Seconds until the idle session goes cold (server TTL, refreshed on every
   *  action). Drives the on-screen countdown + graceful cold-fight ending. */
  expiresInSec: number;
  kind: 'mob' | 'garrison' | 'heist' | 'arena';
  /** Garrison squad progress (turf takeover) or heist room progress: which
   *  defender/room is up, of how many. */
  wave: { index: number; total: number } | null;
  outcome: RoundOutcome | null;
  rewards: { cash: number; xp: number; level: number; leveledUp: boolean } | null;
  loot: LootDrop | null;
  captured: boolean | null;
  result: 'VICTORY' | 'DEFEAT' | null;
  fled: boolean;
  /** Heist-only progress: banked take, whether we're between rooms, etc. */
  heist?: {
    dungeonId: string;
    bankedCash: number;
    awaitingNext: boolean;
    nextEnemyName: string | null;
    timeExpired?: boolean;
    revived?: boolean;
  };
  /** Arena-only: present on the FINISHED view (active:false) of a Pit bout —
   *  the rating/gold settle-up. Null on a fled bout (no-contest). */
  arena?: {
    won: boolean;
    ratingChange: number;
    rating: number;
    title: string;
    wins: number;
    losses: number;
    streak: number;
    goldWon: number;
    goldLost: number;
  } | null;
}

export const startFight = (col: number, row: number) =>
  api.post<FightView>('/mobfight/start', { col, row });
/** Begin an interactive turf takeover vs a district garrison. */
export const startGarrison = (districtId: string) =>
  api.post<FightView>('/mobfight/garrison', { districtId });
/** Begin (or resume) an interactive heist — a multi-room job vs a dungeon. */
export const startHeist = (dungeonId: string) =>
  api.post<FightView>('/mobfight/heist', { dungeonId });
/** Begin (or resume) an interactive arena bout (the Pit) vs a synthesized bot. */
export const startArena = () => api.post<FightView>('/mobfight/arena', {});
export const fightRound = (action: MobAction) =>
  api.post<FightView>('/mobfight/round', { action });
export const fightHeal = () => api.post<FightView>('/mobfight/heal', {});
/** The live bout, or null. Read-only — never starts one, never spends nerve. */
export const fetchCurrentFight = () => api.get<FightView | null>('/mobfight/current');
/** Between heist rooms: press on to the next room (HP + cooldown carry). */
export const continueHeist = () => api.post<FightView>('/mobfight/continue', {});
/**
 * Bail out of the live bout. Always `ok: true`; for a bout other than ARENA
 * that's the whole response (HP just persists, nothing else to report). For
 * ARENA (the Pit), walking out mid-fight is a FORFEIT — it settles exactly
 * like losing the last round (rating drop + stake burn) — so the FightView
 * fields come back too, spread onto the same object, letting the caller show
 * the settled LOSS outcome instead of just closing the screen. Check for
 * `result` before trusting the FightView fields (they're absent otherwise).
 */
export const fightAbandon = () => api.post<{ ok: true } & Partial<FightView>>('/mobfight/abandon', {});
